/**
 * useBuildPriceRange — parallel to useShopPriceRange. Dynamic price-range
 * bounds for the /build filter slider, scoped by selected categoryIds.
 */

import { useEffect, useMemo, useState } from 'react'
import { APIBASE } from '../constants/api.constants'
import { APIROUTES } from '../constants/routes.constants'
import { isNetworkError, SERVER_UNAVAILABLE_MESSAGE } from '../utils/api/server-unavailable'
import type { BuildPriceRangeDto } from './useBuildFilterMeta'

interface Body {
  readonly success: true
  readonly data: { readonly priceRange: BuildPriceRangeDto }
}

export interface UseBuildPriceRangeReturn {
  readonly priceRange: BuildPriceRangeDto
  readonly isLoading: boolean
  readonly error: string | null
}

export function useBuildPriceRange(
  categoryIds: ReadonlyArray<string>
): UseBuildPriceRangeReturn {
  const [priceRange, setPriceRange] = useState<BuildPriceRangeDto>({
    min_paisa: null,
    max_paisa: null
  })
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  const key = useMemo(() => [...categoryIds].sort().join(','), [categoryIds])

  useEffect(() => {
    let cancelled = false
    const ac = new AbortController()
    setIsLoading(true)
    setError(null)
    const url =
      key.length === 0
        ? `${APIBASE}${APIROUTES.BUILDPRICERANGE}`
        : `${APIBASE}${APIROUTES.BUILDPRICERANGE}?categoryIds=${encodeURIComponent(key)}`
    ;(async () => {
      try {
        let res: Response
        try {
          res = await fetch(url, { signal: ac.signal })
        } catch (err) {
          if (cancelled) return
          if (isNetworkError(err)) throw new Error(SERVER_UNAVAILABLE_MESSAGE)
          throw err
        }
        if (cancelled) return
        if (res.status >= 500 || !res.ok) throw new Error(SERVER_UNAVAILABLE_MESSAGE)
        const body = (await res.json()) as Body
        if (!cancelled) setPriceRange(body.data.priceRange)
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : SERVER_UNAVAILABLE_MESSAGE)
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    })()
    return () => { cancelled = true; ac.abort() }
  }, [key])

  return { priceRange, isLoading, error }
}
